const STORAGE_KEY = 'calebasse_floating_button_position_v1';

export interface Position {
  x: number;
  y: number;
}

/**
 * The geometry a position was captured under.
 *
 * Stored alongside {x, y} because the coordinates alone are meaningless on a
 * different-sized screen: "x: 1700" is the right edge at 1920px wide and the
 * middle of the screen at 2400px. Keeping the capture geometry lets the loader
 * re-derive the anchor instead of only clamping, which is one-directional and
 * strands the button mid-screen when the viewport grows back.
 */
export interface PositionGeometry {
  vw: number;
  vh: number;
  w: number;
  h: number;
}

export type StoredPosition = Position & Partial<PositionGeometry>;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function loadPosition(): StoredPosition | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (typeof parsed !== 'object' || parsed === null) return null;
    if (!isFiniteNumber(parsed.x) || !isFiniteNumber(parsed.y)) return null;

    const stored: StoredPosition = { x: parsed.x, y: parsed.y };
    // Geometry is optional: entries written by older builds don't carry it,
    // and the loader falls back to clamping for those.
    if (
      isFiniteNumber(parsed.vw) &&
      isFiniteNumber(parsed.vh) &&
      isFiniteNumber(parsed.w) &&
      isFiniteNumber(parsed.h)
    ) {
      stored.vw = parsed.vw;
      stored.vh = parsed.vh;
      stored.w = parsed.w;
      stored.h = parsed.h;
    }
    return stored;
  } catch {
    return null;
  }
}

export function savePosition(pos: Position, geometry?: PositionGeometry): void {
  try {
    const payload: StoredPosition = { x: pos.x, y: pos.y, ...geometry };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage unavailable (private mode, quota exceeded) — degrade silently
  }
}

